import { useQuery, useMutation, useQueryClient } from 'react-query'
import axios from 'axios'
import { serializeQuery } from './utils'
import { useState } from 'react'
import API from '.'
import { useAuth } from './context/UserAuthContext'

const ENDPOINT = '/api/user'
const QUERY_KEY = 'user'

export const useGetBillingAccess = (config = {}) => {
  const { user } = useAuth()
  const appName = 'billing'
  const queryClient = useQueryClient()
  return useQuery(
    ['access', user?.id || '', appName],
    async () => {
      if (!user) {
        return null
      }
      const data = queryClient.getQueryData(['access', user.id, appName])
      if (data != null) {
        return data
      }
      return (await API.get(`${ENDPOINT}/access/${appName}`)).data?.[appName]
    },
    config
  )
}

export const useGetTyrehubAccess = (config = {}) => {
  const { user } = useAuth()
  const appName = 'tyrehub'
  const queryClient = useQueryClient()
  return useQuery(
    ['access', user?.id || '', appName],
    async () => {
      if (!user) {
        return null
      }
      const data = queryClient.getQueryData(['access', user.id, appName])
      if (data != null) {
        return data
      }
      return (await API.get(`${ENDPOINT}/access/${appName}`)).data?.[appName]
    },
    config
  )
}

export const useGetAccountAccess = (config = {}) => {
  const { user } = useAuth()
  const appName = 'account'
  const queryClient = useQueryClient()
  return useQuery(
    ['access', user?.id || '', appName],
    async () => {
      if (!user) {
        return null
      }
      const data = queryClient.getQueryData(['access', user.id, appName])
      if (data != null) {
        return data
      }
      return (await API.get(`${ENDPOINT}/access/${appName}`)).data?.[appName]
    },
    config
  )
}

export const useGetAllStaff = (query = {}, config = {}) =>
  useQuery(
    [QUERY_KEY, 'list', query],
    async () => (await axios.get(serializeQuery(`${ENDPOINT}/staff`, query))).data,
    config
  )

export const useGetUsers = (query = {}, config = {}) =>
  useQuery(
    [QUERY_KEY, 'list', query],
    async () => (await axios.get(serializeQuery(`${ENDPOINT}/users`, query))).data,
    config
  )

export const useGetUser = (userId, config = {}) =>
  useQuery(
    [QUERY_KEY, 'list', userId],
    async () => (await axios.get(`${ENDPOINT}/user/${userId}`)).data,
    config
  )

export const useGetUserByAuthId = (authId, config = {}) =>
  useQuery(
    [QUERY_KEY, 'authId', authId],
    async () => {
      if (authId) return (await axios.get(`${ENDPOINT}/user/authId/${authId}`)).data
      return null
    },
    { ...config }
  )

export const useCreateUser = (config = {}) =>
  useMutation((newData) => axios.post(ENDPOINT, newData), config)

export const useEditUser = (config = {}) =>
  useMutation((newData) => axios.patch(ENDPOINT, newData), config)

export const useChangePassword = (config = {}) =>
  useMutation((newData) => axios.patch(`${ENDPOINT}/change-password`, newData), config)

export const useEditProfile = (config = {}) =>
  useMutation((newData) => axios.patch(`${ENDPOINT}/edit-profile`, newData), config)

export const useMe = () => {
  const [user, setUser] = useState(null)
  const { refetch, isLoading } = useQuery(
    [QUERY_KEY, 'detail', 'me'],
    async () =>
      (
        await API.get(`${ENDPOINT}/me`, { withCredentials: true }).catch(async (err) => {
          if (err.response?.status === 401) {
            return { data: null }
          }
          throw err
        })
      ).data,
    {
      retry: false,
      refetchOnWindowFocus: true,
      onSuccess: setUser,
    }
  )

  const fetchUser = async () => {
    refetch()
  }

  const clearUser = () => {
    setUser(null)
  }

  return {
    user,
    fetchUser,
    clearUser,
    isLoading,
  }
}

export const useLogin = (config = {}) =>
  useMutation((data) => axios.post(`${ENDPOINT}/login`, data), config)

export const useLogout = (config = {}) =>
  useMutation(() => axios.post(`${ENDPOINT}/logout`), config)

export const useForgotPassword = (config = {}) =>
  useMutation((data) => axios.post(`${ENDPOINT}/forgot-password`, data), config)

export const useResetPassword = (config = {}) =>
  useMutation((data) => axios.patch(`${ENDPOINT}/reset-password`, data), config)

export const useActivateUser = (config = {}) =>
  useMutation((data) => axios.post(`${ENDPOINT}/activate-user`, data), config)

export const useDeactivateUser = (config = {}) =>
  useMutation((data) => axios.post(`${ENDPOINT}/deactivate-user`, data), config)

export const useGetAccounts = (config = {}) =>
  useQuery(
    [QUERY_KEY, 'accounts'],
    async () => (await API.get(`${ENDPOINT}/accounts`)).data,
    config
  )
